import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { ButtonWithLoader, PasswordInput, TextInput, UnauthenticatedLayout } from "src/components";
import { useFormReducer } from "src/hooks";
import { createStyles } from "src/styles";
import { tokens } from "src/styles/figma";
import { z } from "zod";

import { colors } from "@fraction/shared";

export interface LoginProps {
  onSubmit?: (submission?: Record<string, string>) => void;
  isLoading?: boolean;
}

const styles = createStyles({
  input: {
    width: "100%",
    "&:not(:last-child)": {
      marginBottom: 8,
    },
  },
  flexEnd: {
    display: "flex",
    justifyContent: "flex-end",
  },
  forgotPassword: {
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    textDecoration: "none",
    color: colors.palette.GREY_500,
    fontWeight: 500,
    fontSize: 14,
  },
  login: {
    marginTop: 16,
    width: "75%",
    backgroundColor: tokens.colors.green600,
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: 32,
  },
});

const LOGIN = z.object({
  email: z.string().email(),
  password: z.string().min(1, { message: "Required" }),
});

const LoginLayout = ({ isLoading, ...props }: LoginProps) => {
  const { formValues, handleTextChange } = useFormReducer({
    initialValues: {
      email: "",
      password: "",
    },
  });

  const { register, errors, handleSubmit } = useForm({
    resolver: zodResolver(LOGIN),
  });

  const onSubmit = useCallback(() => {
    props.onSubmit?.(formValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onSubmit, formValues]);

  return (
    <UnauthenticatedLayout header="Login to your account">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <TextInput
            variant="v2"
            style={styles.input}
            name="email"
            label="Email"
            defaultValue={formValues.email}
            onChange={handleTextChange}
            type="email"
            autoComplete="email"
            ref={register}
            error={errors?.email?.message}
          />
          <PasswordInput
            variant="v2"
            style={styles.input}
            defaultValue={formValues.password}
            onChange={handleTextChange}
            ref={register}
            error={errors?.password?.message}
            hasVisibilityToggle
          />
        </div>

        <div css={styles.flexEnd}>
          <Link css={styles.forgotPassword} to="/forgot-password">
            Forgot password?
          </Link>
        </div>
        <div css={styles.actions}>
          <ButtonWithLoader style={styles.login} type="submit" loading={isLoading}>
            Login
          </ButtonWithLoader>
        </div>
      </form>
    </UnauthenticatedLayout>
  );
};

export default React.memo(LoginLayout);
